import { MCP_BASE_URL } from '../config';

export class MCPClient {
    private eventSource: EventSource | null = null;
    private baseUrl: string;
    private postUrl: string | null = null;
    private pendingRequests = new Map<number, { resolve: (value: any) => void, reject: (reason?: any) => void }>();

    private initPromise: Promise<void> | null = null;
    private initResolve: (() => void) | null = null;
    private requestCounter = 0;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 3;
    private isReconnecting = false;
 
    constructor(baseUrl?: string) {
        // Use environment variable if available, otherwise fallback to local or provided arg
        this.baseUrl = baseUrl || MCP_BASE_URL;
        this.createNewInitPromise();
    }

    private createNewInitPromise() {
        this.initPromise = new Promise((resolve, reject) => {
            this.initResolve = resolve;
            this.initReject = reject;
        });
    }

    private initReject: ((reason?: any) => void) | null = null;

    private isInitialized = false;
    public get isConnected() { return this.isInitialized; }

    async connect() {
        if (this.eventSource) return this.initPromise;

        // Detect environment
        const isLocal = typeof window !== 'undefined' && 
            (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
        
        // Only use the Vercel proxy if we're explicitly NOT on localhost AND NOT on HuggingFace
        // HuggingFace static spaces do not have a backend to run the /api/sse-proxy
        const isHuggingFace = typeof window !== 'undefined' && window.location.hostname.includes('.hf.space');
        const shouldUseProxy = !isLocal && !isHuggingFace;
        
        const sseEndpoint = shouldUseProxy 
            ? `/api/sse-proxy?v=${Date.now()}` 
            : `${this.baseUrl}/sse?v=${Date.now()}`;

        console.log(`[MCP] Connecting to SSE at ${sseEndpoint}...`);
        this.eventSource = new EventSource(sseEndpoint, { withCredentials: false });

        this.eventSource.onopen = () => {
            console.log('[MCP] SSE Connection Open');
        };

        this.eventSource.onerror = (err) => {
            console.error('[MCP] SSE Error:', err);
            // Auto-reconnect with exponential backoff
            if (!this.isReconnecting && this.reconnectAttempts < this.maxReconnectAttempts) {
                this.isReconnecting = true;
                const backoffMs = 1000 * Math.pow(2, this.reconnectAttempts);
                console.warn(`[MCP] Reconnecting in ${backoffMs}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})...`);
                this.reconnectAttempts++;
                // Tear down current connection
                this.eventSource?.close();
                this.eventSource = null;
                this.postUrl = null;
                this.isInitialized = false;
                this.createNewInitPromise();
                setTimeout(() => {
                    this.isReconnecting = false;
                    this.connect();
                }, backoffMs);
            } else if (this.initReject) {
                this.initReject(new Error("SSE Connection Failed"));
                this.initReject = null;
            }
        };

        this.eventSource.onmessage = (event) => {
            // Check if it's a JSON-RPC message
            try {
                const data = JSON.parse(event.data);
                console.log('MCP Message:', data);

                if (data.id && this.pendingRequests.has(data.id)) {
                    const { resolve, reject } = this.pendingRequests.get(data.id)!;
                    this.pendingRequests.delete(data.id);

                    if (data.error) {
                        reject(new Error(data.error.message || 'Unknown MCP Error'));
                    } else {
                        resolve(data.result);
                    }
                }
            } catch (e) {
                console.log('MCP Non-JSON Message:', event.data);
            }
        };

        this.eventSource.addEventListener('endpoint', (event) => {
            console.log('[MCP] Received Endpoint Event:', event.data);
            const url = event.data; // Usually /messages/?sessionId=xxx
            
            if (shouldUseProxy) {
                // Route POST messages through our Vercel proxy
                // Extract session_id from the endpoint path (MCP SDK uses underscore)
                const sessionMatch = url.match(/session_id=([^&]+)/);
                const sessionId = sessionMatch ? sessionMatch[1] : '';
                this.postUrl = `/api/sse-proxy?sessionId=${sessionId}`;
                console.log('[MCP] POST URL (via Vercel proxy):', this.postUrl);
            } else {
                // Local dev: use backend directly
                if (url.startsWith('http')) {
                    this.postUrl = url;
                } else {
                    const base = this.baseUrl.replace(/\/$/, '');
                    const path = url.startsWith('/') ? url : `/${url}`;
                    this.postUrl = `${base}${path}`;
                }
                console.log('[MCP] POST URL (direct):', this.postUrl);
            }

            // Trigger handshake
            this.performHandshake();
        });

        return this.initPromise;
    }

    async performHandshake() {
        if (this.isInitialized) return;
        console.log("[MCP] Starting Handshake...");
        try {
            // 1. Send 'initialize' request
            const initResult = await this.sendRequest('initialize', {
                protocolVersion: "2024-11-05",
                capabilities: {
                    sampling: {}
                },
                clientInfo: { name: "neura-web", version: "1.0" }
            });
            console.log("[MCP] Initialization Result:", initResult);
            
            // 2. Send 'notifications/initialized' notification
            await this.sendNotification('notifications/initialized');
            
            this.isInitialized = true;
            this.reconnectAttempts = 0; // Reset on successful handshake
            if (this.initResolve) {
                this.initResolve();
                this.initResolve = null;
                this.initReject = null;
            }
            console.log("[MCP] Handshake Complete");
        } catch (e: any) {
            console.error("[MCP] Handshake Failed:", e);
            if (this.initReject) {
                this.initReject(e);
                this.initReject = null;
            }
            // Allow retry by resetting init
            this.isInitialized = false;
        }
    }

    async waitForInitialization(timeoutMs = 15000) {
        if (this.isInitialized) return;
        
        const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error("MCP Initialization Timed Out")), timeoutMs)
        );

        await Promise.race([this.initPromise, timeoutPromise]);
    }

    async callTool(name: string, args: any) {
        await this.waitForInitialization();

        if (!this.postUrl) {
           throw new Error("MCP connection failed: No POST URL");
        }

        console.log(`Calling tool ${name} via MCP at ${this.postUrl}...`, args);
        return this.sendRequest('tools/call', { name, arguments: args || {} });
    }

    async listTools(): Promise<any[]> {
        try {
            await this.waitForInitialization();
            if (!this.postUrl) return [];
            
            const result: any = await this.sendRequest('tools/list', {});
            return result.tools || [];
        } catch (error) {
            console.warn("Failed to list tools:", error);
            return [];
        }
    }

    private async sendNotification(method: string, params: any = {}) {
        if (!this.postUrl) return;
        
        const payload = {
            jsonrpc: "2.0",
            method: method,
            params: params
        };
        console.log(`Sending Notification ${method}:`, payload);

        const response = await fetch(this.postUrl, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(payload),
            credentials: 'include'
        });

        if (!response.ok) {
            const text = await response.text();
            console.warn(`[MCP] Notification ${method} failed:`, response.status, text);
        }
    }

    private async sendRequest(method: string, params: any) {
        const requestId = ++this.requestCounter;
        const payload = {
            jsonrpc: "2.0",
            id: requestId,
            method: method,
            params: params
        };
        console.log(`[MCP] Sending Request ${method}:`, JSON.stringify(payload));

        return new Promise(async (resolve, reject) => {
            const timeout = setTimeout(() => {
                if (this.pendingRequests.has(requestId)) {
                    this.pendingRequests.delete(requestId);
                    reject(new Error(`MCP Request ${method} Timed Out (ID: ${requestId})`));
                }
            }, 120000); // 120s — pipeline syncs can take 30-90s for multi-step operations

            this.pendingRequests.set(requestId, { 
                resolve: (val) => { clearTimeout(timeout); resolve(val); },
                reject: (err) => { clearTimeout(timeout); reject(err); }
            });

            try {
                if (!this.postUrl) {
                    throw new Error("No POST URL available for MCP");
                }
                const response = await fetch(this.postUrl, {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify(payload),
                    credentials: 'include'
                });

                if (!response.ok) {
                    const text = await response.text();
                    console.error(`[MCP] POST Failed:`, response.status, text);
                    throw new Error(`MCP POST Failed: ${response.status} ${text}`);
                }
            } catch (error: any) {
                console.warn('[MCP] POST Error:', error);
                this.pendingRequests.delete(requestId);
                clearTimeout(timeout);
                reject(error);
            }
        });
    }
}
export const mcpClient = new MCPClient();
